/**
 * Analytics API Client
 * Fetches data from InstantDB and the vizi-analytics-worker
 */

import { db, useQuery } from "@/lib/instantdb";
import type { AnalyticsSession, AnalyticsEvent } from "@/lib/instantdb";

const ANALYTICS_API_URL =
  import.meta.env.VITE_ANALYTICS_API_URL ||
  "https://vizi-analytics-worker.vizimix.workers.dev";

export interface OverviewMetrics {
  dau: number;
  mau: number;
  total_sessions: number;
  avg_session_duration_seconds: number;
  total_events: number;
  period_start: string;
  period_end: string;
}

export interface ScreenMetrics {
  screens: Array<{
    screen_name: string;
    view_count: number;
    unique_users: number;
    avg_time_on_screen_seconds: number;
  }>;
  navigation_paths: Array<{
    from_screen: string;
    to_screen: string;
    count: number;
  }>;
}

export interface DeviceMetrics {
  by_platform: Array<{
    platform: string;
    count: number;
    percentage: number;
  }>;
  by_model: Array<{
    model: string;
    count: number;
  }>;
  by_os_version: Array<{
    os_version: string;
    count: number;
  }>;
  by_app_version: Array<{
    app_version: string;
    count: number;
  }>;
}

export interface GeoMetrics {
  by_country: Array<{
    country: string;
    count: number;
    percentage: number;
  }>;
  by_region: Array<{
    country: string;
    region: string;
    count: number;
  }>;
}

async function fetchWithAuth<T>(endpoint: string, days = 30): Promise<T> {
  const url = `${ANALYTICS_API_URL}${endpoint}?days=${days}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

export async function getOverview(days = 30): Promise<OverviewMetrics> {
  return fetchWithAuth<OverviewMetrics>("/api/overview", days);
}

export async function getScreens(days = 30): Promise<ScreenMetrics> {
  return fetchWithAuth<ScreenMetrics>("/api/screens", days);
}

export async function getDevices(days = 30): Promise<DeviceMetrics> {
  return fetchWithAuth<DeviceMetrics>("/api/devices", days);
}

export async function getGeography(days = 30): Promise<GeoMetrics> {
  return fetchWithAuth<GeoMetrics>("/api/geography", days);
}

// ============ InstantDB Queries ============

/**
 * Hook to get real-time analytics sessions from InstantDB
 * Note: Fetches all sessions and filters client-side due to InstantDB index requirements
 */
export function useAnalyticsSessions(days = 30) {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  const result = useQuery({
    analytics_sessions: {
      $: {
        order: { serverCreatedAt: "desc" },
        limit: 10000, // Reasonable limit for client-side filtering
      },
    },
  });

  // Filter sessions client-side to work around index requirement
  const filteredSessions = (result.data?.analytics_sessions ?? []).filter(
    (session) =>
      (session.startedAt as number) && (session.startedAt as number) > startDate
  );

  return {
    ...result,
    data: result.data
      ? { ...result.data, analytics_sessions: filteredSessions }
      : undefined,
  };
}

/**
 * Hook to get real-time analytics events from InstantDB
 * Note: Fetches all events and filters client-side due to InstantDB index requirements
 */
export function useAnalyticsEvents(days = 7) {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  const result = useQuery({
    analytics_events: {
      $: {
        order: { serverCreatedAt: "desc" },
        limit: 10000,
      },
    },
  });

  // Filter events client-side to work around index requirement
  const filteredEvents = (result.data?.analytics_events ?? []).filter(
    (event) =>
      (event.timestamp as number) && (event.timestamp as number) > startDate
  );

  return {
    ...result,
    data: result.data
      ? { ...result.data, analytics_events: filteredEvents }
      : undefined,
  };
}

/**
 * Hook to get error events from InstantDB
 * Note: Fetches error events and filters by time client-side
 */
export function useAnalyticsErrors(days = 30) {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  const result = useQuery({
    analytics_events: {
      $: {
        where: { eventType: "error" },
        order: { serverCreatedAt: "desc" },
        limit: 5000,
      },
    },
  });

  // Filter by timestamp client-side to work around index requirement
  const filteredErrors = (result.data?.analytics_events ?? []).filter(
    (event) =>
      (event.timestamp as number) && (event.timestamp as number) > startDate
  );

  return {
    ...result,
    data: result.data
      ? { ...result.data, analytics_events: filteredErrors }
      : undefined,
  };
}

/**
 * One-time query to get sessions (for non-reactive use)
 * Note: Fetches all sessions and filters client-side
 */
export async function getRealtimeSessions(
  days = 30,
): Promise<AnalyticsSession[]> {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  const { data } = await db.queryOnce({
    analytics_sessions: {
      $: {
        order: { serverCreatedAt: "desc" },
        limit: 10000,
      },
    },
  });

  // Filter client-side to work around index requirement
  return ((data?.analytics_sessions ?? []) as AnalyticsSession[]).filter(
    (session) => session.startedAt && session.startedAt > startDate
  );
}

/**
 * One-time query to get events (for non-reactive use)
 * Note: Fetches all events and filters client-side
 */
export async function getRealtimeEvents(days = 7): Promise<AnalyticsEvent[]> {
  const startDate = Date.now() - days * 24 * 60 * 60 * 1000;

  const { data } = await db.queryOnce({
    analytics_events: {
      $: {
        order: { serverCreatedAt: "desc" },
        limit: 10000,
      },
    },
  });

  // Filter client-side to work around index requirement
  return ((data?.analytics_events ?? []) as AnalyticsEvent[]).filter(
    (event) => event.timestamp && event.timestamp > startDate
  );
}

// ============ Computed Analytics from InstantDB ============

/**
 * Compute overview metrics from InstantDB sessions
 */
export function computeOverviewFromSessions(
  sessions: AnalyticsSession[],
  days = 30,
): OverviewMetrics {
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const startDate = now - days * dayMs;
  const monthStart = now - 30 * dayMs;

  // Filter sessions within time range
  const filteredSessions = sessions.filter((s) => s.startedAt >= startDate);
  const monthSessions = sessions.filter((s) => s.startedAt >= monthStart);

  // Get unique users for DAU (today) and MAU (last 30 days)
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const todayUsers = new Set(
    filteredSessions
      .filter((s) => s.startedAt >= todayStart)
      .map((s) => s.userId),
  );
  const monthUsers = new Set(monthSessions.map((s) => s.userId));

  // Calculate average session duration
  const sessionsWithDuration = filteredSessions.filter(
    (s) => s.durationSeconds != null,
  );
  const avgDuration =
    sessionsWithDuration.length > 0
      ? sessionsWithDuration.reduce(
          (sum, s) => sum + (s.durationSeconds ?? 0),
          0,
        ) / sessionsWithDuration.length
      : 0;

  // Count total events (page views)
  const totalEvents = filteredSessions.reduce((sum, s) => sum + s.pageCount, 0);

  return {
    dau: todayUsers.size,
    mau: monthUsers.size,
    total_sessions: filteredSessions.length,
    avg_session_duration_seconds: Math.round(avgDuration),
    total_events: totalEvents,
    period_start: new Date(startDate).toISOString(),
    period_end: new Date(now).toISOString(),
  };
}

/**
 * Compute device metrics from InstantDB sessions
 */
export function computeDeviceMetricsFromSessions(
  sessions: AnalyticsSession[],
): DeviceMetrics {
  const platformCounts: Record<string, number> = {};
  const modelCounts: Record<string, number> = {};
  const osVersionCounts: Record<string, number> = {};
  const appVersionCounts: Record<string, number> = {};

  for (const session of sessions) {
    // Platform
    platformCounts[session.platform] =
      (platformCounts[session.platform] ?? 0) + 1;

    // Device type (using as model proxy)
    const model = `${session.browser} on ${session.deviceType}`;
    modelCounts[model] = (modelCounts[model] ?? 0) + 1;

    // OS version
    const osKey = session.osVersion || "Unknown";
    osVersionCounts[osKey] = (osVersionCounts[osKey] ?? 0) + 1;

    // App version
    appVersionCounts[session.appVersion] =
      (appVersionCounts[session.appVersion] ?? 0) + 1;
  }

  const total = sessions.length || 1;

  return {
    by_platform: Object.entries(platformCounts)
      .map(([platform, count]) => ({
        platform,
        count,
        percentage: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.count - a.count),
    by_model: Object.entries(modelCounts)
      .map(([model, count]) => ({ model, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10),
    by_os_version: Object.entries(osVersionCounts)
      .map(([os_version, count]) => ({ os_version, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10),
    by_app_version: Object.entries(appVersionCounts)
      .map(([app_version, count]) => ({ app_version, count }))
      .sort((a, b) => b.count - a.count),
  };
}

/**
 * Compute screen metrics from InstantDB events
 */
export function computeScreenMetricsFromEvents(
  events: AnalyticsEvent[],
): ScreenMetrics {
  const pageViews = events.filter((e) => e.eventType === "page_view");

  // Screen view counts
  const screenCounts: Record<
    string,
    { views: number; users: Set<string>; totalTime: number; timeCount: number }
  > = {};

  for (const event of pageViews) {
    const screen = event.routePath ?? event.eventName;
    if (!screenCounts[screen]) {
      screenCounts[screen] = {
        views: 0,
        users: new Set(),
        totalTime: 0,
        timeCount: 0,
      };
    }
    screenCounts[screen].views++;
    screenCounts[screen].users.add(event.userId);
    if (event.timeOnPreviousPageSeconds != null) {
      screenCounts[screen].totalTime += event.timeOnPreviousPageSeconds;
      screenCounts[screen].timeCount++;
    }
  }

  // Navigation paths
  const pathCounts: Record<string, number> = {};
  for (const event of pageViews) {
    if (event.previousRoutePath && event.routePath) {
      const pathKey = `${event.previousRoutePath}|${event.routePath}`;
      pathCounts[pathKey] = (pathCounts[pathKey] ?? 0) + 1;
    }
  }

  return {
    screens: Object.entries(screenCounts)
      .map(([screen_name, data]) => ({
        screen_name,
        view_count: data.views,
        unique_users: data.users.size,
        avg_time_on_screen_seconds:
          data.timeCount > 0 ? Math.round(data.totalTime / data.timeCount) : 0,
      }))
      .sort((a, b) => b.view_count - a.view_count),
    navigation_paths: Object.entries(pathCounts)
      .map(([key, count]) => {
        const [from_screen, to_screen] = key.split("|");
        return { from_screen, to_screen, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 20),
  };
}
